import { request } from './client'

/* **W-135 — WHAT HAS TO BE LOADED BEFORE THE FIRST SHIFT RUNS.** Sprint 53.

   Pack 5's own sentence for the screen: *"This screen exists for one week and is then never
   opened again. It is here because go-live data loading is consistently the thing nobody
   owns, and a checklist with counts against it is cheaper than a spreadsheet emailed
   round."*

   Eleven rows in dependency order, each with what it is, where it comes from, how many are
   expected, how many are loaded, what it waits for, and what state that puts it in. */

/** The eleven, by wire name. A union so that a twelfth added on the server stops the
 *  screen compiling rather than drawing a blank sentence in an Arabic table. */
export type GoLiveStep =
  | 'PROPERTIES_AND_FLOORS'
  | 'ROOM_TYPES'
  | 'ROOMS'
  | 'PUBLIC_AREAS'
  | 'CHECKLIST_TEMPLATES'
  | 'CLEANING_RULES_PER_ROOM'
  | 'STOCK_ITEMS'
  | 'STOCK_LOCATIONS'
  | 'OPENING_STOCK_COUNT'
  | 'PEOPLE_AND_ROLES'
  | 'FIRST_ROSTER'

/** What kind of work fills a row. The pack draws it as a column and it earns one: the
 *  physical count is the only row that needs somebody walking round with a clipboard,
 *  *"worth booking now rather than the day before"*. */
export type GoLiveSource = 'BY_HAND' | 'SPREADSHEET' | 'IN_BULK' | 'PHYSICAL_COUNT'

/**
 * **`WAITING` AND `BLOCKED` ARE DIFFERENT AND THE DIFFERENCE IS WHOSE PROBLEM IT IS.**
 * Waiting means the thing it needs has not been done yet; blocked means the thing it needs
 * cannot be done by anybody in the hotel. `LOADED` is the one this build added: something
 * is there and nobody has said how much there should be, so nothing calls it finished.
 */
export type GoLiveState =
  | 'DONE' | 'SHORT' | 'LOADED' | 'NOT_STARTED' | 'WAITING' | 'BLOCKED'

export interface GoLiveRow {
  step: GoLiveStep
  source: GoLiveSource
  /** Null where nobody has said how many there should be. */
  expected: number | null
  /** True where the product works the figure out and the hotel cannot contradict it. */
  derived: boolean
  loaded: number
  /** What this row waits for, in the order the pack draws them. */
  needs: GoLiveStep[]
  state: GoLiveState
}

export interface GoLiveView {
  propertyId: string
  propertyName: string
  propertyNameAr: string | null
  /** The pack's *"7 of 11 complete"*. Only genuinely finished rows are counted. */
  completeCount: number
  stepCount: number
  /** False for the whole of this phase — TQ-01. See the server's `GoLiveView`. */
  signInReady: boolean
  rows: GoLiveRow[]
}

export const fetchGoLive = (
  propertyId: string,
  options: { signal?: AbortSignal } = {},
) => request<GoLiveView>(
  `/properties/${propertyId}/go-live`,
  options.signal ? { signal: options.signal } : {},
)

/**
 * The hotel's own figure for one row. **Zero clears it** rather than storing a target of
 * none — see the server's `SetGoLiveTarget`.
 *
 * It returns the WHOLE checklist rather than the row, because a target changes the count at
 * the top and the state of the row it belongs to; a screen patching one row from a partial
 * reply would show "7 of 11" above eight finished rows until somebody refreshed.
 */
export const setGoLiveTarget = (
  propertyId: string,
  step: GoLiveStep,
  expected: number,
  options: { signal?: AbortSignal } = {},
) => request<GoLiveView>(`/properties/${propertyId}/go-live/${step}`, {
  method: 'PUT',
  body: { expected },
  ...(options.signal ? { signal: options.signal } : {}),
})
